import moment from 'moment';
// import {useHistory} from "react-router-dom";
import { SEARCH_PARAMS_VALUES, SEARCH_PARAM_KEYS } from '../constants/app.constants';
import { History, Location } from 'history';
import { filters } from './filters/filters';
import { DateTime } from 'luxon';
import { SpotType } from '@rapid-recovery-agency-inc/insightt-graphql';

export const getURL = () => {
  return new URL(document.URL);
};

export const getSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams;
};

export const getSearchParamsFromProp = (queryString: string) => {
  const searchParams = new URLSearchParams(queryString);
  return searchParams;
};

export const getSearchParam = (name: string, history: History<unknown> | null = null) => {
  let searchParams;
  if (history) {
    searchParams = new URLSearchParams(history.location.search);
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }
  const value = searchParams.get(name);
  if (!value) return null;
  return value;
};

export const setSearchParam = (
  name: string,
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  value: any,
  history: History<unknown> | null = null,
  branch?: number,
) => {
  let searchParams;
  if (history) {
    searchParams = new URLSearchParams(history.location.search);
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }
  const currentValue = searchParams.get(name);
  if (currentValue === null) {
    searchParams.append(name, value);
  }
  if (currentValue === value) {
    return;
  }
  searchParams.set(name, value);
  if (history) {
    //set branch id in search params
    if (branch) {
      searchParams.set(SEARCH_PARAM_KEYS.branch, branch.toString());
    }
    history.push(`${history.location.pathname}?${searchParams.toString()}`);
  } else {
    window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
  }
};

export const deleteSearchParam = (name: string, history: History<unknown> | null = null) => {
  let searchParams;
  if (history) {
    searchParams = new URLSearchParams(history.location.search);
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }
  searchParams.delete(name);
  if (history) {
    history.push(`${history.location.pathname}?${searchParams}`);
  } else {
    window.history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const getQueryString = (obj: Record<string, any>) => {
  return new URLSearchParams(obj).toString();
};

export const getParamsObject = () => {
  const searchParams = getSearchParams();

  const searchParamsObject = Array.from(searchParams).reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    {} as Record<string, any>,
  );

  return searchParamsObject;
};

export const updateSearchParamsAndNavigate = (
  location: Location<unknown>,
  history: History<unknown>,
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  newSearchParamsObj: Record<string, any>,
) => {
  // Get the current search parameters
  const searchParams = getSearchParamsFromProp(location.search);

  // Update the search parameters
  Object.entries(newSearchParamsObj).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  // Update the location object's search property
  const queryParamString = searchParams.toString();

  const newUrl = `${location.pathname}?${queryParamString}`;

  // Navigate to the updated URL
  history.push(newUrl);
};

export const updateSearchParamsAndReplace = (
  location: Location<unknown>,
  history: History<unknown>,
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  newSearchParamsObj: Record<string, any>,
) => {
  // Get the current search parameters
  const searchParams = getSearchParamsFromProp(location.search);

  // Update the search parameters
  Object.entries(newSearchParamsObj).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  // Update the location object's search property
  const queryParamString = searchParams.toString();

  const newUrl = `${location.pathname}?${queryParamString}`;

  // Navigate to the updated URL
  history.replace(newUrl);
};

export const getQueryStringOverview = () => {
  const queryParamsOverview = {
    [filters.branch.urlKey]: 0,
    [SEARCH_PARAM_KEYS.branch]: 0,
    [SEARCH_PARAM_KEYS.year]: DateTime.now().year,
    [SEARCH_PARAM_KEYS.month]: DateTime.now().month - 1,
    // [filters.startDateTime.urlKey]: DateTime.now().startOf('month'),
    // [filters.endDateTime.urlKey]: handleEndDate(
    //   DateTime.now().endOf('month'),
    //   true,
    // ).toISO(),
    // [filters.dateType.urlKey]: DateRangeType.MONTH,
    [SEARCH_PARAM_KEYS.recoveryTab]: SEARCH_PARAMS_VALUES.recoveryTab.totalRepossessions,
    [SEARCH_PARAM_KEYS.spottersTab]: SpotType.TOTAL,
    [SEARCH_PARAM_KEYS.camerasTab]: SEARCH_PARAMS_VALUES.camerasTab.totalScans,
  };

  return getQueryString(queryParamsOverview);
};

const doesItNeedHash = (path: string) => {
  const segmentedUrl = path.split('/');
  const module = segmentedUrl[2];
  if (module === 'reports') {
    if (segmentedUrl.includes('client')) {
      return 'MTDYTDClientsStatsTableId';
    }

    if (
      segmentedUrl.includes('top-recovery-agent') ||
      segmentedUrl.includes('top-spotters') ||
      segmentedUrl.includes('top-camera-cars') ||
      segmentedUrl.includes('agent-repossession')
    ) {
      return 'ranking-top-drivers-container';
    }

    if (segmentedUrl.includes('employee-stats')) {
      return 'ranking-employee-stats-container';
    }
  }

  if (module === 'shift') {
    if (segmentedUrl.includes('agent-repossession')) {
      return 'ranking-top-drivers-container';
    }
  }

  // default
  return '';
};

// eslint-disable-next-line complexity
function getCurrentParamsThroughRedirects(path: string, searchParams: string) {
  // branch is a global param.
  const branchId = getParamsObject()[SEARCH_PARAM_KEYS.branch] || 0;
  const segmentedPath = path.split('/');
  const currentParams = new URLSearchParams(searchParams);
  const realPath = path.endsWith('/') ? path.slice(0, -1) : path;
  if (
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.overview ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopRecovery ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopSpotters ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopCameraCars
  ) {
    const year = getParamsObject()[SEARCH_PARAM_KEYS.year] || moment().year();
    const month = getParamsObject()[SEARCH_PARAM_KEYS.month] || moment().month();

    currentParams.set(SEARCH_PARAM_KEYS.branch, `${branchId}`);
    currentParams.set(SEARCH_PARAM_KEYS.year, `${year}`);
    currentParams.set(SEARCH_PARAM_KEYS.month, `${month}`);

    return currentParams.toString();
  }

  if (
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.reports ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.reportsAllTimes ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.reportsDaily ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.reportsDailyMap ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.clientDetailReport ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.lenderDetailReport ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.lienholderDetailReport ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.lenderLienholderDetailReport ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopRecoveryReports ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopSpottersReports ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.viewAllTopCameraCarsReports ||
    realPath === SEARCH_PARAMS_VALUES.urlRedirect.activeShifts
  ) {
    currentParams.set(SEARCH_PARAM_KEYS.branch, `${branchId}`);

    // to mantain the time selector between client table in reports and clientd details
    if (getParamsObject()[SEARCH_PARAM_KEYS.timeSelector]) {
      const timeSelector = getParamsObject()[SEARCH_PARAM_KEYS.timeSelector];
      currentParams.set(SEARCH_PARAM_KEYS.clientListTab, timeSelector);
    }

    return currentParams.toString();
  }

  if (segmentedPath.includes('my-stats')) {
    const queryParams = getParamsObject();
    const dateTab = queryParams[SEARCH_PARAM_KEYS.dateTab] || SEARCH_PARAMS_VALUES.dateTab.daily;
    const startDate = queryParams[SEARCH_PARAM_KEYS.startDate];
    const endDate = queryParams[SEARCH_PARAM_KEYS.endDate];

    currentParams.set(SEARCH_PARAM_KEYS.dateTab, dateTab);
    currentParams.set(SEARCH_PARAM_KEYS.startDate, startDate);
    currentParams.set(SEARCH_PARAM_KEYS.endDate, endDate);

    return currentParams.toString();
  }

  if (segmentedPath.includes('basic-dashboard')) {
    const queryParams = getParamsObject();
    const selectedTab = queryParams[SEARCH_PARAM_KEYS.basicDashboardTab];
    const orderType = queryParams[SEARCH_PARAM_KEYS.orderType];

    currentParams.set(SEARCH_PARAM_KEYS.branch, branchId);
    currentParams.set(SEARCH_PARAM_KEYS.basicDashboardTab, selectedTab);
    currentParams.set(SEARCH_PARAM_KEYS.orderType, orderType);

    return currentParams.toString();
  }

  if (segmentedPath.includes('basic-dashboard-admin')) {
    const queryParams = getParamsObject();
    const selectedTab = queryParams[SEARCH_PARAM_KEYS.basicDashboardTab];
    const listTab = queryParams[SEARCH_PARAM_KEYS.basicDashboardListTab];
    const orderType = queryParams[SEARCH_PARAM_KEYS.orderType];
    const switchTab = queryParams[SEARCH_PARAM_KEYS.basicDashboardSwitch];

    currentParams.set(SEARCH_PARAM_KEYS.branch, branchId);
    currentParams.set(SEARCH_PARAM_KEYS.basicDashboardTab, selectedTab);
    currentParams.set(SEARCH_PARAM_KEYS.basicDashboardListTab, listTab);
    currentParams.set(SEARCH_PARAM_KEYS.orderType, orderType);
    currentParams.set(SEARCH_PARAM_KEYS.basicDashboardSwitch, switchTab);

    return currentParams.toString();
  }

  throw new Error('No redirect params found');
}

interface RedirectObject {
  pathname: string;
  search: string;
  hash?: string;
}

export const redirectUrl = () => {
  const currentPath = window.location.pathname;

  const queryParams = getParamsObject();

  const urlFrom = queryParams[SEARCH_PARAM_KEYS.from];

  const url = new URL(urlFrom);

  const { pathname, search } = url;
  const redirectSearch = getCurrentParamsThroughRedirects(pathname, search);

  const redirectObject: RedirectObject = {
    pathname,
    search: redirectSearch,
  };

  const hash = doesItNeedHash(currentPath);
  if (hash) {
    redirectObject.hash = `#${hash}`;
  }

  return redirectObject;
};

/**
 * Function to adjust the margin of the X icon of popover in product tour
 * @param iconToLeft - if true, the icon will be moved to the left
 * @returns
 */
export const adjustPopoverCloseIcon = (iconToLeft = false): void => {
  const marginRightInPx = iconToLeft ? 375 : 0;
  const element = document?.querySelector('.reactour__popover');
  const closeElements = element?.querySelector('.close');

  if (!closeElements) {
    return;
  }

  if (closeElements instanceof HTMLElement) {
    closeElements.style.marginRight = `${marginRightInPx}px`;
  }
};
